import * as React from "react";

import { useRequiredContext } from "replo-utils/react/context";

type FileDropHandler = (files: File[]) => void;
type AcceptedAssetTypes = ("image" | "video")[];

type InterceptorConfig = {
  handler: FileDropHandler;
  acceptedTypes: AcceptedAssetTypes;
};

type ChatFileDropZoneContextValue = {
  interceptor: InterceptorConfig | null;
  setInterceptor: (config: InterceptorConfig | null) => void;
};

const ChatFileDropZoneContext =
  React.createContext<ChatFileDropZoneContextValue | null>(null);

export const ChatFileDropZoneProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [interceptor, setInterceptor] =
    React.useState<InterceptorConfig | null>(null);

  const value = React.useMemo(
    () => ({
      interceptor,
      setInterceptor,
    }),
    [interceptor],
  );

  return (
    <ChatFileDropZoneContext.Provider value={value}>
      {children}
    </ChatFileDropZoneContext.Provider>
  );
};

export const useChatFileDropZoneContext = () => {
  return useRequiredContext(ChatFileDropZoneContext);
};

export function useChatFileDropZone({
  isActive,
  acceptedTypes = ["image", "video"],
  onFileDrop,
}: {
  isActive: boolean;
  acceptedTypes?: AcceptedAssetTypes;
  onFileDrop: (files: File[]) => void;
}) {
  const { setInterceptor } = useChatFileDropZoneContext();

  // NOTE (Kevin, 2025-12-15): Use a ref to store the latest onFileDrop callback
  // to avoid re-running the effect when the callback reference changes.
  const onFileDropRef = React.useRef(onFileDrop);
  React.useEffect(() => {
    onFileDropRef.current = onFileDrop;
  }, [onFileDrop]);

  // NOTE (Kevin, 2025-12-15): Use a ref to store acceptedTypes to avoid re-running
  // the effect when a new array with the same values is passed on each render.
  // Without this, passing `acceptedTypes: ["image", "video"]` inline creates a new
  // array reference each render, causing setInterceptor to update context state,
  // which triggers a re-render, creating an infinite loop.
  const acceptedTypesRef = React.useRef(acceptedTypes);
  acceptedTypesRef.current = acceptedTypes;

  React.useEffect(() => {
    if (isActive) {
      setInterceptor({
        handler: (files: File[]) => onFileDropRef.current(files),
        acceptedTypes: acceptedTypesRef.current,
      });
      return () => {
        setInterceptor(null);
      };
    }
  }, [isActive, setInterceptor]);
}
